import { chain, get, map, keyBy, isEqual, pick, omit, tail, isNil, isFunction, isFinite } from "lodash"
import { numberFormatter, smallNumberFormatter, percentageFormatter, scaledPercentageFormatter } from "helpers/formatters/numbers"
import { sanitizePositionSet, createMetricTooltip } from "gatherers/helpers"
import { getSharpeLabel } from "helpers/metrics/utils"
import { getResidualAttributionSummarySubquery, getResidualPeriodPerformanceSubquery, RESIDUAL_RETURN } from "helpers/residualAttribution"

function computeTotalSharpeRatio(data) {
  const annualizedReturn = get(data, "data.model.simulation.periodPerformance.annualizedReturns")
  const annualizedVolatility = get(data, "data.model.simulation.periodPerformance.annualizedVolatility")
  if (!isFinite(annualizedReturn) || !isFinite(annualizedVolatility) || annualizedVolatility === 0) {
    return null
  }
  return annualizedReturn / annualizedVolatility
}

function getTotalSharpeTooltip(data, options) {
  if (options.isActive) {
    return null
  }
  const label = getSharpeLabel(options)
  const excessSharpe = get(data, "data.model.simulation.periodPerformance.informationRatio")
  const annualizedRiskFreeRate = get(data, "data.model.simulation.periodPerformance.annualizedRiskFreeRate")
  const excessFormatted = isFinite(excessSharpe) ? numberFormatter(excessSharpe) : "—"
  const rfrFormatted = isFinite(annualizedRiskFreeRate)
    ? scaledPercentageFormatter(annualizedRiskFreeRate)
    : "Not Defined"
  return [
    `${label} (Excess): ${excessFormatted}`,
    `RFR: ${rfrFormatted}`,
  ]
}

/**
 * Extracts warning messages from GraphQL response by message code
 * @param {Object} data - The GraphQL response data
 * @param {string} code - The message code to find (e.g., 'DECISION_QUALITY_SUPPRESSED')
 * @returns {string|undefined} The matching warning message, or undefined if none found
 */
function getWarningMessageByCode(data, code) {
  const messages = get(data, "apiMessages.messages", [])
  const matchingMessage = messages.find(msg => msg.code === code)
  return matchingMessage?.message
}

export const TOTAL_RETURN = "totalReturn"
export const ALPHA_RETURN = "alphaReturn"
export const FACTORS_RETURN = "factorsReturn"
export const TRADING_RETURN = "tradingReturn"
export { RESIDUAL_RETURN }

export const ANNUALIZED_RETURNS = "annualizedReturns"
export const ANNUALIZED_VOLATILITY = "annualizedVolatility"
export const SHARPE_RATIO = "sharpeRatio"
export const ALPHA_ANNUALIZED_RETURNS = "alphaAnnualizedReturns"
export const ALPHA_ANNUALIZED_VOLATILITY = "alphaAnnualizedVolatility"
export const ALPHA_SHARPE_RATIO = "alphaSharpeRatio"
export const FACTORS_ANNUALIZED_RETURNS = "factorsAnnualizedReturns"
export const FACTORS_ANNUALIZED_VOLATILITY = "factorsAnnualizedVolatility"
export const FACTORS_SHARPE_RATIO = "factorsSharpeRatio"
export const RESIDUAL_ANNUALIZED_RETURNS = "residualAnnualizedReturns"
export const RESIDUAL_ANNUALIZED_VOLATILITY = "residualAnnualizedVolatility"
export const SORTINO_RATIO = "sortinoRatio"
export const RETURNS_CORRELATION = "returnsCorrelation"
export const MAX_DRAWDOWN = "maxDrawdown"
export const FACTOR_ANNUALIZED_RETURNS = "factorAnnualizedReturns"
export const FACTOR_ANNUALIZED_VOLATILITY = "factorAnnualizedVolatility"
export const FACTOR_SHARPE_RATIO = "factorSharpeRatio"
export const BATTING_TOTAL = "battingTotal"
export const BATTING_ALPHA = "battingAlpha"
export const BATTING_FACTORS = "battingFactors"
export const SLUGGING_TOTAL = "sluggingTotal"
export const SLUGGING_ALPHA = "sluggingAlpha"
export const SLUGGING_FACTORS = "sluggingFactors"

// Residual metric definition - defined separately for reuse in tooltip
const RESIDUAL_METRIC = {
  label: "Residual",
  valueGetter: data => get(data, "data.model.simulation.performance.0.attribution.summary.residual.cumulativePercent"),
  formatter: percentageFormatter,
}

const METRICS = {
  [TOTAL_RETURN]: {
    label: "Total Return",
    valueGetter: data => get(data, "data.model.simulation.performance.0.totalReturn.cumulativePercent"),
    formatter: percentageFormatter,
    tooltipGetter: createMetricTooltip(
      RESIDUAL_METRIC,
      (formattedValue) => `Total return includes a residual of ${formattedValue}`,
    ),
  },
  [ALPHA_RETURN]: {
    label: "Alpha",
    valueGetter: data => get(data, "data.model.simulation.performance.0.attribution.summary.specific.cumulativePercent"),
    formatter: percentageFormatter,
  },
  [FACTORS_RETURN]: {
    label: "Factors",
    valueGetter: data => get(data, "data.model.simulation.performance.0.attribution.summary.factors.cumulativePercent"),
    formatter: percentageFormatter,
  },
  [TRADING_RETURN]: {
    label: "Trading",
    valueGetter: data => get(data, "data.model.simulation.performance.0.attribution.summary.trading.cumulativePercent"),
    formatter: percentageFormatter,
  },
  [RESIDUAL_RETURN]: RESIDUAL_METRIC,
  [ANNUALIZED_RETURNS]: {
    label: "Annualized Returns",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.annualizedReturns"),
    formatter: scaledPercentageFormatter,
  },
  [ANNUALIZED_VOLATILITY]: {
    label: "Annualized Volatility",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.annualizedVolatility"),
    formatter: scaledPercentageFormatter,
  },
  [SHARPE_RATIO]: {
    label: (props) => `${getSharpeLabel(props)} (Total)`,
    valueGetter: computeTotalSharpeRatio,
    formatter: numberFormatter,
    tooltipGetter: getTotalSharpeTooltip,
    tooltipPlacement: "value",
  },
  [ALPHA_ANNUALIZED_RETURNS]: {
    label: "Annualized Returns",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.specific.annualizedReturns"),
    formatter: scaledPercentageFormatter,
  },
  [ALPHA_ANNUALIZED_VOLATILITY]: {
    label: "Annualized Volatility",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.specific.annualizedVolatility"),
    formatter: scaledPercentageFormatter,
  },
  [ALPHA_SHARPE_RATIO]: {
    label: getSharpeLabel,
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.specific.informationRatio"),
    formatter: numberFormatter,
  },
  [FACTORS_ANNUALIZED_RETURNS]: {
    label: "Annualized Returns",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.factors.annualizedReturns"),
    formatter: scaledPercentageFormatter,
  },
  [FACTORS_ANNUALIZED_VOLATILITY]: {
    label: "Annualized Volatility",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.factors.annualizedVolatility"),
    formatter: scaledPercentageFormatter,
  },
  [FACTORS_SHARPE_RATIO]: {
    label: getSharpeLabel,
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.factors.informationRatio"),
    formatter: numberFormatter,
  },
  [RESIDUAL_ANNUALIZED_RETURNS]: {
    label: "Annualized Returns",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.residual.annualizedReturns"),
    formatter: scaledPercentageFormatter,
  },
  [RESIDUAL_ANNUALIZED_VOLATILITY]: {
    label: "Annualized Volatility",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.residual.annualizedVolatility"),
    formatter: scaledPercentageFormatter,
  },
  [SORTINO_RATIO]: {
    label: "Sortino Ratio",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.sortinoRatio"),
    formatter: numberFormatter,
  },
  [RETURNS_CORRELATION]: {
    label: "Return Correlation",
    valueGetter: data => get(data, "data.model.simulation.correlation"),
    formatter: numberFormatter,
  },
  [MAX_DRAWDOWN]: {
    label: "Max Drawdown",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.maxDrawdown.drawdown"),
    formatter: scaledPercentageFormatter,
  },
  [FACTOR_ANNUALIZED_RETURNS]: {
    label: "Annualized Returns",
    valueGetter: (data, factorId) => chain(data)
      .get("data.model.simulation.periodPerformance.attribution.factors")
      .find(({ id }) => id === factorId)
      .get("annualizedReturns")
      .value(),
    formatter: scaledPercentageFormatter,
  },
  [FACTOR_ANNUALIZED_VOLATILITY]: {
    label: "Annualized Volatility",
    valueGetter: (data, factorId) => chain(data)
      .get("data.model.simulation.periodPerformance.attribution.factors")
      .find(({ id }) => id === factorId)
      .get("annualizedVolatility")
      .value(),
    formatter: scaledPercentageFormatter,
  },
  [FACTOR_SHARPE_RATIO]: {
    label: getSharpeLabel,
    valueGetter: (data, factorId) => chain(data)
      .get("data.model.simulation.periodPerformance.attribution.factors")
      .find(({ id }) => id === factorId)
      .get("informationRatio")
      .value(),
    formatter: numberFormatter,
  },
  [BATTING_TOTAL]: {
    label: "Total",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.decisionQuality.batting"),
    formatter: smallNumberFormatter,
  },
  [BATTING_ALPHA]: {
    label: "Alpha",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.specific.decisionQuality.batting"),
    formatter: smallNumberFormatter,
  },
  [BATTING_FACTORS]: {
    label: "Factors",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.factors.decisionQuality.batting"),
    formatter: smallNumberFormatter,
  },
  [SLUGGING_TOTAL]: {
    label: "Total",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.decisionQuality.slugging"),
    formatter: smallNumberFormatter,
  },
  [SLUGGING_ALPHA]: {
    label: "Alpha",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.specific.decisionQuality.slugging"),
    formatter: smallNumberFormatter,
  },
  [SLUGGING_FACTORS]: {
    label: "Factors",
    valueGetter: data => get(data, "data.model.simulation.periodPerformance.attribution.summary.factors.decisionQuality.slugging"),
    formatter: smallNumberFormatter,
  },
}

function getPerformanceSubquery(isRfrAttributionEnabled) {
  return `performance {
        totalReturn {
          cumulativePercent
        }
        attribution {
          summary {
            specific { cumulativePercent }
            factors { cumulativePercent }
            trading { cumulativePercent }
            ${isRfrAttributionEnabled ? getResidualAttributionSummarySubquery(true) : ""}
          }
          factors {
            id
            return {
              cumulativePercent
            }
          }
        }
      }`
}

function getPeriodPerformanceSubquery(hasDecisionQuality, isRfrAttributionEnabled) {
  const decisionQualityFields = hasDecisionQuality ? `
          decisionQuality {
            batting
            slugging
          }` : ""

  return `periodPerformance(useModelRiskFreeRate: $useModelRiskFreeRate) {
        maxDrawdown {
          drawdown
        }
        sortinoRatio
        annualizedReturns
        annualizedVolatility
        informationRatio
        annualizedRiskFreeRate
        ${decisionQualityFields}
        attribution {
          summary {
            factors {
              annualizedReturns
              annualizedVolatility
              informationRatio
              ${decisionQualityFields}
            }
            specific {
              annualizedReturns
              annualizedVolatility
              informationRatio
              ${decisionQualityFields}
              }
            ${isRfrAttributionEnabled ? getResidualPeriodPerformanceSubquery() : ""}
          }
          factors {
            id
            annualizedReturns
            annualizedVolatility
            informationRatio
          }
        }
      }`
}

function buildPeriodPerformanceQuery(hasDecisionQuality, isRfrAttributionEnabled, shouldUseIntradayQuery = false) {
  const performanceSubquery = getPerformanceSubquery(isRfrAttributionEnabled)
  const periodPerformanceSubquery = getPeriodPerformanceSubquery(hasDecisionQuality, isRfrAttributionEnabled)

  // For single-day intraday: performance from intradaySimulation, periodPerformance from simulation
  if (shouldUseIntradayQuery) {
    return `#single-period-performance
query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $source: PositionSetInput!, $on: Date!, $batchId: Id, $from: Date!, $to: Date!, $baseOverride:[PositionSetInput], $useModelRiskFreeRate: Boolean) {
  model(id: $modelId) {
    intradayPerformance: intradaySimulation(positionSet: $positionSet, base: $base, on: $on, batchId: $batchId) {
      ${performanceSubquery}
    }
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: END_DATE) {
      ${periodPerformanceSubquery}
      correlation(with: $source, baseOverride: $baseOverride)
    }
  }
}`
  }

  return `#single-period-performance
query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $source: PositionSetInput!, $from: Date!, $to: Date!, $baseOverride:[PositionSetInput], $useModelRiskFreeRate: Boolean) {
  model(id: $modelId) {
    simulation(positionSet: $positionSet, base: $base, from: $from, to: $to, interval: END_DATE) {
      ${performanceSubquery}
      ${periodPerformanceSubquery}
      correlation(with: $source, baseOverride: $baseOverride)
    }
  }
}`
}

// When intradaySimulation is aliased, remap performance into the simulation path
// so existing valueGetters (data.model.simulation.performance) still work
function withIntradayPerformance(rawData) {
  const intradayPerf = get(rawData, "data.model.intradayPerformance.performance")
  if (!intradayPerf) return rawData
  const lastPerf = intradayPerf.slice(-1)
  return { ...rawData, data: { ...rawData.data, model: { ...rawData.data.model, simulation: { ...rawData.data.model.simulation, performance: lastPerf } } } }
}

function makePeriodPerformanceSelect(metrics, options) {
  return (rawData) => {
    const data = withIntradayPerformance(rawData)
    const warningMessage = getWarningMessageByCode(data, DECISION_QUALITY_SUPPRESSED_CODE)

    const values = map(metrics, metric => {
      const value = METRICS[metric].valueGetter(data)
      const label = isFunction(METRICS[metric].label) ? METRICS[metric].label(options) : METRICS[metric].label
      const isDecisionQualityMetric = DECISION_QUALITY_METRICS.includes(metric)

      const warning = isDecisionQualityMetric && isNil(value) && warningMessage
        ? warningMessage
        : undefined

      const tooltip = METRICS[metric].tooltipGetter
        ? METRICS[metric].tooltipGetter(data, options)
        : undefined

      return {
        ...METRICS[metric],
        id: metric,
        label,
        value,
        ...(warning && { warning }),
        ...(tooltip && { tooltip }),
      }
    })

    const summary = chain(values)
      .head()
      .value()

    return {
      summary,
      values: keyBy(tail(values), "id"),
    }
  }
}

function makeFactorPerformanceSelect(metrics, factorId, options) {
  return (rawData) => {
    const data = withIntradayPerformance(rawData)
    const values = map(metrics, (metric) => ({
      ...METRICS[metric],
      label: isFunction(METRICS[metric].label) ? METRICS[metric].label(options) : METRICS[metric].label,
      id: metric,
      value: METRICS[metric].valueGetter(data, factorId),
    }))

    const summary = chain(data)
      .get("data.model.simulation.performance")
      .last()
      .get("attribution.factors")
      .find(({ id }) => id === factorId)
      .get("return.cumulativePercent")
      .value()

    return {
      summary,
      values: keyBy(values, "id"),
    }
  }
}

const DECISION_QUALITY_METRICS = [
  BATTING_TOTAL,
  BATTING_ALPHA,
  BATTING_FACTORS,
  SLUGGING_TOTAL,
  SLUGGING_ALPHA,
  SLUGGING_FACTORS,
]

export const DECISION_QUALITY_SUPPRESSED_CODE = "DECISION_QUALITY_SUPPRESSED"

export function createPeriodPerformanceSingleDateQuery(modelId, positionSet, base, correlationSource, from, to, metrics, options = {}) {
  const { contextBase, factorId, isIntradayMode, intradayEnabled, isRfrAttributionEnabled = false, intradayPriceRefreshTime } = options
  const isEffectiveIntradayMode = isIntradayMode && !!intradayEnabled
  const shouldUseIntradaySimulation = isEffectiveIntradayMode && from === to && !options.batchId
  const isActive = !!contextBase

  const hasBaseOverride = base && !isEqual(
    pick(base, ["id", "type"]),
    pick(contextBase, ["id", "type"]),
  )

  // Check if any decision quality metrics are requested
  const needsDecisionQuality = metrics.some(metric => DECISION_QUALITY_METRICS.includes(metric))
  const query = buildPeriodPerformanceQuery(needsDecisionQuality, isRfrAttributionEnabled, shouldUseIntradaySimulation)

  const key = [
    "periodPerformance",
    "singleDate",
    modelId,
    positionSet,
    from,
    to,
    hasBaseOverride,
    shouldUseIntradaySimulation,
    needsDecisionQuality,
    isRfrAttributionEnabled,
    ...(isActive ? [base] : []),
    ...(intradayPriceRefreshTime ? [intradayPriceRefreshTime] : []),
  ]

  return {
    key,
    query,
    variables: {
      modelId,
      positionSet: sanitizePositionSet(positionSet),
      base: isActive ? base : null,
      source: sanitizePositionSet(omit(correlationSource, ["fxHedgeEnabled"])),
      from,
      to,
      ...(shouldUseIntradaySimulation && { on: from, batchId: options.batchId }),
      ...(hasBaseOverride && { baseOverride: contextBase }),
      useModelRiskFreeRate: !isActive && isRfrAttributionEnabled,
    },
    select: factorId ? makeFactorPerformanceSelect(metrics, factorId, { isRfrAttributionEnabled, isActive }) : makePeriodPerformanceSelect(metrics, { isRfrAttributionEnabled, isActive }),
  }
}
